import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BooleanCellComponent,
  ButtonCellComponent,
  EmployeeCellComponent,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportSettingsProps,
  WorkExperienceReportCustomParams,
  WorkExperienceReportData,
} from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import {
  WorkExperienceReportRow,
  processReportData,
} from '~/scenes/PortalModules/Analytics/People/WorkExperienceReport/data';
import { getLargerNumber } from '~/utils/helper';
import { getFormattedDateString } from '~/utils/helpers/date';

type PropsType = {
  data: WorkExperienceReportData[];
  requestProps: AnalyticsReportSettingsProps<WorkExperienceReportCustomParams>;
};

export const useWorkExperienceReport = (props: PropsType) => {
  const { data, requestProps } = props;

  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { getUnitByIdIncludeDeleted } = useAllOrgUnits();

  const reportParams = useMemo(() => {
    return {
      deductAbsences: !!requestProps.params?.deductAbsences,
      extExperience: !!requestProps.params?.extExperience,
    };
  }, [requestProps.params]);

  const { rows, columnWidths } = useMemo(() => {
    const rows = processReportData(
      data,
      getEmployeeById,
      getUnitByIdIncludeDeleted,
      (years, months) => {
        return `${years} ${t('laYears')} ${months} ${t('laMonths')}`;
      },
      reportParams,
    );

    const colsCharCount = rows.reduce(
      (counts, entry) => {
        return {
          ...counts,
          totalExperienceYearsAndMonths: getLargerNumber(
            counts.totalExperienceYearsAndMonths,
            entry.totalExperienceYearsAndMonths.length,
          ),
        };
      },
      {
        totalExperienceDays: t('inDays').length,
        totalExperienceYearsAndMonths: t('inYearsAndMonths').length,
      },
    );

    const charMultiplier = 9;
    const columnWidths = {
      totalExperienceDays: colsCharCount.totalExperienceDays * charMultiplier,
      totalExperienceYearsAndMonths:
        colsCharCount.totalExperienceYearsAndMonths * charMultiplier,
      includeExternal: t('laPreviousCompany').length * charMultiplier,
      deductLongAbsences: t('longAbsencesDeducted').length * charMultiplier,
    };

    return { rows, columnWidths };
  }, [data, getEmployeeById, getUnitByIdIncludeDeleted, t, reportParams]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const formatDateCell = (date: Date | undefined) =>
      getFormattedDateString(date, 'L');

    const employeeColumnConfigs: GridColDef<WorkExperienceReportRow>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      { field: 'unitNumber', headerName: t('laUnitNumber') },
      {
        field: 'manager',
        headerName: t('laManager'),
        minWidth: 130,
      },
      {
        field: 'originalStartDate',
        headerName: t('laEmpOrigStartDate'),
        valueFormatter: ({ value }) => formatDateCell(value),
        minWidth: 150,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        valueFormatter: ({ value }) => formatDateCell(value),
      },
    ];

    const totalWorkExperienceColumns: GridColDef<WorkExperienceReportRow>[] = [
      {
        field: 'totalExperienceDays',
        headerName: t('inDays'),
        type: 'number',
        minWidth: 100,
        width: columnWidths.totalExperienceDays,
      },
      {
        field: 'totalExperienceYearsAndMonths',
        headerName: t('inYearsAndMonths'),
        minWidth: 100,
        width: columnWidths.totalExperienceYearsAndMonths,
      },
    ];

    const externalExperienceColumn = reportParams.extExperience
      ? [
          {
            field: 'hasExternalExperience',
            headerName: t('laPreviousCompany'),
            renderCell: BooleanCellComponent,
            minWidth: 100,
            width: columnWidths.includeExternal,
          },
        ]
      : [];

    const deductAbsencesColumn =
      reportParams.deductAbsences || reportParams.extExperience
        ? [
            {
              field: 'hasDeductedLongAbsences',
              headerName: t('longAbsencesDeducted'),
              renderCell: BooleanCellComponent,
              minWidth: 100,
              width: columnWidths.deductLongAbsences,
            },
          ]
        : [];

    const conditionalColumns: GridColDef<WorkExperienceReportRow>[] =
      externalExperienceColumn.concat(deductAbsencesColumn);

    const columnConfigs = [
      ...employeeColumnConfigs,
      ...totalWorkExperienceColumns,
      ...conditionalColumns,
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'totalWorkExperience',
        headerName: t('totalWorkExperience'),
        children: getFieldNamesFromColumnConfig(
          totalWorkExperienceColumns.concat(conditionalColumns),
        ),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, columnWidths, reportParams]);

  return {
    table: {
      columnConfigs,
      rows,
      columnGroupingModel,
    },
  };
};
